import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SMALL_SCREEN_QUERY } from '@constants/sidebar';
import { useMediaQuery } from '@hooks/useMediaQuery';

const HOME_PATH = '/';

interface PageState {
  readonly pathname: string;
  readonly open: boolean | null;
  readonly expanded: boolean;
}

export interface AppsFooterState {
  readonly open: boolean;
  readonly expanded: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly setExpanded: (expanded: boolean) => void;
}

const freshPage = (pathname: string): PageState => ({ pathname, open: null, expanded: false });

/**
 * The row of apps starts open on Home and collapsed everywhere else, and on
 * small screens always collapsed. What the user opens or closes, the gallery
 * included, lasts until the next page.
 */
export const useAppsFooterState = (): AppsFooterState => {
  const { pathname } = useLocation();
  const smallScreen = useMediaQuery(SMALL_SCREEN_QUERY);
  const [stored, setStored] = useState<PageState>(() => freshPage(pathname));
  const page = stored.pathname === pathname ? stored : freshPage(pathname);
  if (page !== stored) setStored(page);

  const setOpen = useCallback((open: boolean) => setStored((prev) => ({ ...prev, open })), []);
  const setExpanded = useCallback(
    (expanded: boolean) => setStored((prev) => ({ ...prev, expanded })),
    [],
  );

  return {
    open: page.open ?? (pathname === HOME_PATH && !smallScreen),
    expanded: page.expanded,
    setOpen,
    setExpanded,
  };
};
